<template>
  <AuthShell>
    <v-form v-if="!passwordRequestEmailSent" class="auth-form" autocomplete="off" @submit.prevent="passwordSending">
      <h1 class="auth-form__title">Reset your password</h1>
      <p class="auth-form__sub">Enter your email address and we'll send a reset link.</p>

      <div class="auth-form__field">
        <label for="reset-email" class="auth-form__label">Email address</label>
        <v-text-field
          id="reset-email"
          v-model="userEmail"
          type="email"
          placeholder="you@clinic.com"
          autocomplete="off"
          :error-messages="!isEmailValid && formButtonClicked ? 'Please enter a valid email address' : ''" />
      </div>

      <v-btn color="primary" block size="large" type="submit" :loading="showPreloader">Send reset link</v-btn>

      <v-alert v-if="errors.length > 0" type="error" variant="tonal">
        <div v-for="error in errors" :key="error">{{ error }}</div>
      </v-alert>

      <p class="auth-form__center">
        <router-link :to="{ name: 'login' }" class="auth-form__link">Back to sign in</router-link>
      </p>
    </v-form>

    <div v-else class="auth-form">
      <h1 class="auth-form__title">Check your email</h1>
      <p class="auth-form__sub">
        If that address has an account, a reset link is on its way. The link expires in 24 hours.
      </p>
      <p class="auth-form__center">
        <router-link :to="{ name: 'login' }" class="auth-form__link">Back to sign in</router-link>
      </p>
    </div>
  </AuthShell>
</template>

<script setup>
import { ref, computed } from "vue";
import authService from "@/services/authService";
import { tryParseApiErrors } from "@/utils";
import { FORM_VALIDATION_PATTERNS } from "@/utils/constants";
import AuthShell from "@/components/AuthShell.vue";

const errors = ref([]);
const showPreloader = ref(false);
const passwordRequestEmailSent = ref(false);
const userEmail = ref("");
const formButtonClicked = ref(false);

// Validators
const isEmailValid = computed(() => {
  const email = userEmail.value;
  return email !== "" && FORM_VALIDATION_PATTERNS.EMAIL_PATTERN.test(email);
});

const passwordSending = async () => {
  formButtonClicked.value = true;

  if (isEmailValid.value) {
    showPreloader.value = true;
    try {
      await authService.passwordResetRequest(userEmail.value);
      passwordRequestEmailSent.value = true;
      userEmail.value = "";
      errors.value = [];
    } catch (error) {
      errors.value = tryParseApiErrors(error);
    } finally {
      showPreloader.value = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/pages/_auth-form.scss";
</style>
